<template>
  <div data-scroll-el="warranty-form">
    <base-form v-if="!submitted" class="grid" :form @submit="submit">
      <div class="mb-8">
        <p class="mb-8 uppercase title-2">
          {{ $t.warrantyForm.itemUnderWarranty }}
        </p>
        <p class="title-4 mb-4">
          {{ $t.warrantyForm.completeForm }}
        </p>

        <p class="mb-4">
          {{ $t.warrantyForm.instructionsTitle }}
        </p>

        <ul class="mb-4 ml-4 list-disc list-inside">
          <li v-for="(instruction, key) in $t.warrantyForm.instructions" :key>
            {{ instruction }}
          </li>
        </ul>

        <p class="fw-medium">
          {{ $t.warrantyForm.warrantyJourney }}
        </p>
      </div>

      <div class="mb-8">
        <p class="mb-6 pb-4 b-b b-grey-50 uppercase title-2">
          {{ $t.warrantyForm.contactInformation }}
        </p>

        <div class="mb-4 gap-x-3 gap-y-4 grid md:cols-2">
          <vf-form-field
            v-slot="{ attrs }"
            name="firstName"
            :rule="[
              ...getValidator('firstName', $t.firstName),
              validateRequired($t.firstName),
              validateName($t.validation.firstName),
            ]"
          >
            <vf-input v-model="form.firstName" v-bind="attrs" :disabled="processing" required>
              {{ $t.firstName }}
            </vf-input>
          </vf-form-field>
          <vf-form-field
            v-slot="{ attrs }"
            name="lastName"
            :rule="[
              ...getValidator('lastName', $t.lastName),
              validateRequired($t.lastName),
              validateName($t.validation.lastName),
            ]"
          >
            <vf-input v-model="form.lastName" v-bind="attrs" :disabled="processing" required>
              {{ $t.lastName }}
            </vf-input>
          </vf-form-field>
        </div>
        <div class="gap-x-3 gap-y-4 grid md:cols-2">
          <vf-form-field
            v-slot="{ attrs }"
            :hint="$t.emailFormatHint"
            name="consumerEmail"
            :rule="[...getValidator('email', $t.email), validateEmail()]"
          >
            <vf-input v-model="form.consumerEmail" v-bind="attrs" :disabled="processing" required type="email">
              {{ $t.email }}
            </vf-input>
          </vf-form-field>
          <vf-form-field
            v-slot="{ attrs }"
            name="phone"
            :rule="form.phone !== '' ? [validateRequired($t.phone), validatePhone(form.phoneCode)] : []"
          >
            <input-phone
              v-bind="attrs"
              v-model="form.phone"
              v-model:code="form.phoneCode"
              :disabled="processing"
              use-country-code
            >
              {{ $t.phone }}
            </input-phone>
          </vf-form-field>
        </div>
      </div>

      <div class="mb-8">
        <p class="mb-6 pb-4 b-b b-grey-50 uppercase title-2">
          {{ $t.orderDetails }}
        </p>

        <div class="mb-4 gap-x-3 gap-y-4 grid md:cols-2">
          <vf-form-field
            v-slot="{ attrs }"
            name="orderNumber"
            :rule="[
              ...getValidator('orderNumber', $t.orderNumber),
              validateMaxLength(10),
              validateRequired($t.orderNumber),
            ]"
          >
            <vf-input
              v-model="form.orderNumber"
              v-bind="attrs"
              :disabled="processing"
              :hint="$t.warrantyForm.orderNumberHint"
              required
            >
              {{ $t.orderNumber }}
              <template #end>
                <vf-toggletip placement="top-start">
                  <template #trigger>
                    <span class="sr-only">{{ $t.orderNumber }}</span>
                    <vf-icon name="info" size="md" />
                  </template>
                  <img :alt="$t.orderNumber" src="https://assets.thenorthface.eu/image/upload/v1724863348/order-number-hint">
                </vf-toggletip>
              </template>
            </vf-input>
          </vf-form-field>
        </div>

        <div class="mb-4">
          <p class="text-md">
            {{ $t.warrantyForm.attachment }}
          </p>
          <p>{{ $t.warrantyForm.attachmentHint }}</p>
          <vf-file-upload v-model="form.attachments" :max-files="3" />
        </div>

        <div class="mb-4 gap-x-3 gap-y-4 grid md:cols-2">
          <vf-form-field
            v-slot="{ attrs }"
            name="itemNumber"
            :rule="[...getValidator('itemNumber', $t.warrantyForm.itemNumberLabel)]"
          >
            <vf-input v-model="form.itemNumber" v-bind="attrs" :disabled="processing">
              {{ $t.warrantyForm.itemNumberLabel }}

              <template #end>
                <vf-toggletip placement="top-start">
                  <template #trigger>
                    <span class="sr-only">{{ $t.orderNumber }}</span>
                    <vf-icon name="info" size="md" />
                  </template>
                  {{ $t.warrantyForm.itemNumberHint }}
                </vf-toggletip>
              </template>
            </vf-input>
          </vf-form-field>
        </div>

        <vf-form-field
          v-slot="{ attrs }"
          name="message"
          :rule="[validateRequired($t.message), validateRegex(/^[^<>]*$/, $t.noSpecialCharacters)]"
        >
          <vf-textarea
            v-model="form.message"
            v-bind="attrs"
            data-test-id="form-contact-us-message"
            :disabled="processing"
            :props-textarea="{ rows: 11 }"
            required
            resizable
          >
            {{ $t.warrantyForm.messageLabel }}
          </vf-textarea>
        </vf-form-field>
      </div>

      <div>
        <p class="mb-6 pb-4 b-b b-grey-50 uppercase title-2">
          {{ $t.warrantyForm.pickUp }}
        </p>

        <vf-form-field
          v-slot="{ attrs }"
          name="preferredDate"
          :rule="pickUpType !== 'drop' ? validateRequired('', $t.validators.requiredSelect) : []"
        >
          <div class="mb-4 gap-y-3 grid">
            <vf-radio v-model="pickUpType" v-bind="attrs" name="type" value="carrier">
              {{ $t.warrantyForm.carrierLabel }}
            </vf-radio>
            <vf-radio v-model="pickUpType" v-bind="attrs" name="type" value="drop">
              {{ $t.warrantyForm.dropOffLabel }}
            </vf-radio>
          </div>
        </vf-form-field>

        <vf-form-field
          v-if="pickUpType === 'carrier'"
          v-slot="{ attrs }"
          name="preferredDate"
          :rule="[
            validateRequired($t.warrantyForm.pickUpLabel),
            validateDate(),
            validateWeekday($t.warrantyForm.weekdayValidation),
          ]"
        >
          <vf-input
            v-bind="attrs"
            v-model="form.preferredDate"
            data-test-id="input-calendar"
            :max="maxDate"
            :min="minDate"
            required
            type="date"
          >
            {{ $t.warrantyForm.pickUpLabel }}
          </vf-input>
        </vf-form-field>
        <p v-if="pickUpType === 'drop'">
          <base-interpolator :content="$t.warrantyForm.dropOff">
            <template #delivery="{ args: [message] }">
              <base-link class="underlined" target="_blank" to="https://www.ups.com/dropoff">
                {{ message }}
              </base-link>
            </template>
          </base-interpolator>
        </p>
      </div>

      <div v-if="pickUpType === 'carrier'" class="mt-8">
        <p class="mb-6 pb-4 b-b b-grey-50 uppercase title-2">
          Address of item pick up
        </p>

        <div class="mb-4">
          <vf-form-field
            v-slot="{ attrs }"
            name="lastName"
            :rule="[
              ...getValidator('lastName', $t.lastName),
              validateRequired($t.lastName),
              validateName($t.validation.lastName),
            ]"
          >
            <vf-input v-model="form.country" v-bind="attrs" disabled required>
              {{ $t.country }}
            </vf-input>
          </vf-form-field>
        </div>
        <div class="mb-4">
          <vf-form-field
            v-slot="{ attrs }"
            name="addressLine1"
            :rule="[
              ...getValidator('addressLine1', $t.addressLine1),
              validateRequired($t.addressLine1),
            ]"
          >
            <vf-input v-model="form.addressLine1" v-bind="attrs" :disabled="processing" required>
              {{ $t.addressLine1 }}
            </vf-input>
          </vf-form-field>
        </div>
        <div class="mb-4">
          <vf-form-field
            v-slot="{ attrs }"
            name="addressLine2"
            :rule="[
              ...getValidator('addressLine2', $t.addressLine2),
            ]"
          >
            <vf-input v-model="form.addressLine2" v-bind="attrs" :disabled="processing">
              {{ $t.addressLine2 }}
            </vf-input>
          </vf-form-field>
        </div>
        <div class="gap-x-3 gap-y-4 grid md:cols-2">
          <vf-form-field
            v-slot="{ attrs }"
            name="city"
            :rule="[
              ...getValidator('city', $t.city),
              validateRequired($t.city),
            ]"
          >
            <vf-input v-model="form.city" v-bind="attrs" :disabled="processing" required>
              {{ $t.city }}
            </vf-input>
          </vf-form-field>
          <vf-form-field
            v-slot="{ attrs }"
            name="postcode"
            :rule="[
              ...getValidator('postalCode', $t.zipCode, {
                minLength: $t.validation.postalCodeLength,
                pattern: $t.validation.postalCode,
              }),
            ]"
          >
            <vf-input v-model="form.postcode" v-bind="attrs" :disabled="processing" required>
              {{ $t.zipCode }}
            </vf-input>
          </vf-form-field>
        </div>
      </div>

      <div class="mb-8 mt-6 pt-4 b-t b-grey-50">
        <vf-form-field
          v-slot="{ attrs }"
          name="policy"
          :rule="[validateRequired('', $t.validators.requiredCheckbox)]"
        >
          <vf-checkbox v-model="form.policy" v-bind="attrs">
            <form-legal-disclaimer :content="$t.brandAgreement.policy" />
          </vf-checkbox>
        </vf-form-field>
      </div>

      <vf-button class="mb-4 w-full md:w-1/4" :disabled="processing" :loading="processing" type="submit">
        {{ submitted ? $t.messageSent : $t.submit }}
      </vf-button>
    </base-form>

    <div v-if="submitted">
      <p class="mb-8 title-2">
        {{ $t.warrantyForm.successTitle }}
      </p>
      <ul class="ml-4 list-disc list-inside">
        <li v-for="(instruction, key) in $t.warrantyForm.instructions" :key>
          {{ instruction }}
        </li>
      </ul>
      <div v-if="reviewSubmission" class="mt-4">
        <div class="mb-6">
          <p class="title-3 mb-4 b-b b-grey-50 uppercase">
            {{ $t.warrantyForm.contactInformation }}
          </p>
          <div>
            <p>{{ form.firstName }} {{ form.lastName }}</p>
            <p>{{ form.consumerEmail }}</p>
            <p>{{ form.phoneCode }}{{ form.phone }}</p>
          </div>
        </div>

        <div class="mb-6">
          <p class="title-3 mb-4 b-b b-grey-50 uppercase">
            {{ $t.orderDetails }}
          </p>
          <div>
            <p>{{ $t.orderNumber }}: {{ form.orderNumber }}</p>
            <p>{{ $t.warrantyForm.itemNumberLabel }}: {{ form.itemNumber }}</p>
            <p>{{ form.message }}</p>
          </div>
        </div>

        <div class="mb-6">
          <p class="title-3 mb-4 b-b b-grey-50 uppercase">
            {{ $t.warrantyForm.pickUp }}
          </p>
          <div>
            <p v-if="form.preferredDate">
              {{ $t.warrantyForm.pickUpLabel }}: {{ form.preferredDate }}
            </p>
          </div>
        </div>

        <div v-if="form.addressLine1" class="mb-6">
          <p class="title-3 mb-4 b-b b-grey-50 uppercase">
            {{ $t.warrantyForm.pickUpAddress }}
          </p>
          <div>
            <p>{{ form.country }}</p>
            <p>{{ form.addressLine1 }}</p>
            <p>{{ form.addressLine2 }}</p>
            <p>{{ form.postcode }}</p>
          </div>
        </div>
      </div>

      <div class="mt-6 flex gap-4 <md:f-col">
        <vf-button to="/">
          {{ $t.returnHome }}
        </vf-button>
        <vf-button v-if="!reviewSubmission" variant="tertiary" @click="review()">
          {{ $t.warrantyForm.review }}
        </vf-button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const { $t } = useNuxtApp()
const { warranty } = useApi()
const { validateEmail, validateRequired, validateMaxLength } = useLocalizedValidators()
const { load: loadRules, getValidator } = useAddressApiRules()

const recaptcha = useRecaptcha()
const countryCode = useCountryCode()
const toast = useToaster()

const processing = ref(false)
const submitted = ref(false)
const reviewSubmission = ref(false)

const pickUpType = ref<'carrier' | 'drop'>()

// Min pickup date is 4 days from now
const minDate = new Date(Date.now() + 3456e5).toISOString().split('T')[0]
// Max pickup date is 14 days from now
const maxDate = new Date(Date.now() + 12096e5).toISOString().split('T')[0]

const initialState = {
  personTitle: '',
  firstName: '',
  lastName: '',
  consumerEmail: '',
  country: countryCode,
  addressLine1: '',
  addressLine2: '',
  city: '',
  postcode: '',
  phone: '',
  policy: false,
  itemNumber: '',
  preferredDate: '' as string,
  phoneCode: getPhoneCodeByCountryCode(countryCode),
  orderNumber: '',
  message: '',
  attachments: [] as File[]
} as const

const form = reactive({ ...initialState })

watch(pickUpType, () => form.preferredDate = pickUpType.value === 'carrier' ? minDate : '')

const validateWeekday = (message: string) => (value: Date) => {
  const date = new Date(value)
  const isWeekday = date.getDay() !== 0 && date.getDay() !== 6

  return !isWeekday && message
}

const review = () => {
  reviewSubmission.value = true
  scrollToElement('warranty-form')
}

const submit = async () => {
  const [lang, country] = useLocale().split('-')
  const captchaResponse = await recaptcha.execute('submitWarrantyCase')
  const formData = new FormData()
  const phone = formatLocalNumberToE164(form.phone, form.phoneCode)

  formData.append('origin', `${country}-${lang}`)
  formData.append('firstName', form.firstName)
  formData.append('lastName', form.lastName)
  formData.append('consumerEmail', form.consumerEmail)
  formData.append('consumerPhone', form.phoneCode + phone)
  formData.append('orderNumber', form.orderNumber)
  formData.append('itemNumber', form.itemNumber)
  formData.append('message', form.message)
  formData.append('countrySelected', form.country)
  formData.append('addressLine1', form.addressLine1)
  formData.append('addressLine2', form.addressLine2)
  formData.append('city', form.city)
  formData.append('postcode', form.postcode)
  formData.append('preferredDate', form.preferredDate)
  formData.append('category', 'Clothing')
  formData.append('service', 'Warranty')
  formData.append('subject', 'Submit a warranty claim')

  form.attachments.forEach((attachment) => formData.append('attachments', attachment))

  processing.value = true

  const { data, error } = await warranty.createSupportCase({ captchaResponse }, formData as any)

  if (error.value || data.value?.status !== 'success') {
    toast.add({
      props: {
        message: $t.somethingWentWrong,
        type: 'error'
      }
    })
  }
  else {
    submitted.value = true
  }

  scrollToElement('warranty-form')
  processing.value = false
}

onMounted(() => loadRules(countryCode, useLocale()))
</script>
